<template>
  <div id="user-list">
    <v-row class="mb-5"></v-row>

    <!-- list filters -->
    <v-card>
      <v-card-title>
        <v-row>
          <v-col>
            Search &amp; Filter
          </v-col>

          <v-col
            align="end"
            class="pb-0"
          >
            <v-tooltip
              v-if="$can('create', 'Academy')"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  fab
                  x-small
                  elevation="4"
                  color="primary"
                  :to="{ name: 'academy-create' }"
                  v-on="on"
                >
                  <v-icon>
                    {{ icons.mdiPlus }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Add New Academy</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-title>

      <!-- table -->
      <vuex-table
        v-sortable-data-table
        :headers="tableColumns"
        :items="academies"
        :options="options"
        :search.sync="search"
        :meta.sync="meta"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': [5, 10, 25, 50, 100]
        }"
        item-key="id"
        :item-class="renderRowClass"
        @sorted="handleOrder"
        @update:options="o => options = o"
      >
        <!-- name -->
        <template #[`item.title`]="{item}">
          <div class="d-flex align-center">
            <v-avatar
              :color="item.thumbUrl ? '' : 'primary'"
              :class="item.thumbUrl ? '' : 'v-avatar-light-bg primary--text'"
              size="32"
            >
              <v-img
                v-if="item.thumbUrl"
                :src="item.thumbUrl"
              ></v-img>
              <span
                v-else
                class="font-weight-medium"
              >{{ avatarText(item.title) }}</span>
            </v-avatar>

            <div class="d-flex flex-column ms-3">
              <router-link
                :to="{ name : 'academy-view', params : { id : item.id } }"
                class="text--primary font-weight-semibold text-truncate cursor-pointer text-decoration-none"
              >
                {{ item.title | textEllipsis(120) }}
              </router-link>
            </div>
          </div>
        </template>

        <!-- actions -->
        <template #[`item.actions`]="{item}">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="me-1"
                v-bind="attrs"
                :to="{ name: 'academy-view', params: { id: item.id } }"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiEyeOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>View Academy</span>
          </v-tooltip>

          <v-tooltip
            v-if="$can('update', 'Academy')"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="me-1"
                v-bind="attrs"
                :to="{ name: 'academy-edit', params: { id: item.id } }"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiPencil }}
                </v-icon>
              </v-btn>
            </template>
            <span>Edit Academy</span>
          </v-tooltip>

          <v-tooltip
            v-if="$can('destroy', 'Academy')"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="error"
                v-bind="attrs"
                :disabled="deleting"
                :loading="isDialogOpen"
                @click="selectAcademyToDelete(item)"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>Delete Academy</span>
          </v-tooltip>
        </template>
      </vuex-table>
    </v-card>

    <academy-delete-dialog
      v-if="$can('destroy', 'Academy')"
      :data="selectedAcademies"
      :deleting="deleting"
      :is-dialog-open.sync="isDialogOpen"
    ></academy-delete-dialog>
  </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import { mdiDotsVertical, mdiEyeOutline, mdiPencil, mdiPlus, mdiDeleteOutline, mdiExportVariant } from '@mdi/js'

// eslint-disable-next-line object-curly-newline
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from '@vue/composition-api'

// sidebar
import { avatarText } from '@core/utils/filter'
import AcademyDeleteDialog from './AcademyDeleteDialog.vue'

import useAcademyList from './useAcademyList'

export default {
  components: {
    AcademyDeleteDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const isDialogOpen = ref(false)

    const deleting = computed(() => vm.$store.getters['academies/deleting'])
    const selectedAcademies = ref(null)
    const selectAcademyToDelete = academies => {
      selectedAcademies.value = academies
      isDialogOpen.value = !!academies
    }

    const {
      tableColumns,
      totalAcademiesListTable,
      loading,

      meta,
      search,
      filters,
      options,
      roleFilter,

      setOptions,
      setFilters,

      academies,
      academiesListTable,
      academiesTotalLocal,

      handleReorder,

      init,
      destroy,
    } = useAcademyList()

    onMounted(() => {
      init()
    })
    onUnmounted(() => {
      destroy()
    })

    const handleOrder = e => {
      const movedItem = Array.from(academies.value).splice(e.oldIndex, 1)[0]
      const replacedItem = Array.from(academies.value).splice(e.newIndex, 1)[0]

      // console.error({ movedItem, replacedItem })
      // academies.splice(e.newIndex, 0, movedItem)

      handleReorder({
        id: movedItem.id,
        sort: replacedItem.sort,
      })
    }

    const renderRowClass = item => `academy-${item.id}`

    return {
      academiesListTable,
      tableColumns,
      totalAcademiesListTable,

      loading,
      academiesTotalLocal,
      avatarText,

      setOptions,
      setFilters,

      academies,
      meta,
      search,
      filters,
      options,
      roleFilter,

      // icons
      icons: {
        mdiPlus,
        mdiPencil,
        mdiEyeOutline,
        mdiDotsVertical,
        mdiDeleteOutline,
        mdiExportVariant,
      },

      isDialogOpen,

      deleting,
      selectedAcademies,
      selectAcademyToDelete,

      handleOrder,
      renderRowClass,
    }
  },
}
</script>

<style lang="scss">
@import '@core/preset/preset/apps/user.scss';
</style>
